/**
 * CertificateForm — ficha de un certificado TLS a PANTALLA COMPLETA.
 *
 * Mismo patrón que el resto de fichas (`FormCardsShell` + `FormCardsHead`):
 * topbar con volver/miga/contador, cabecera con título en línea y badges,
 * contenido en tarjetas a dos columnas y guardado con ✓/✗.
 *
 * No hay pestaña de registros: un certificado es una fila. Lo importante es
 * la caducidad (días restantes) y a qué dominio pertenece.
 */

import React, { useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckIcon,
  KeyIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormDirtyDock from './common/FormDirtyDock';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';
import Dialog from './common/Dialog';
import { getServerFormStyle } from '../utils/serverFormStyle';

const TIPOS = [
  { value: 'letsencrypt', label: "Let's Encrypt" },
  { value: 'ovh', label: 'OVH SSL' },
  { value: 'digicert', label: 'DigiCert' },
  { value: 'sectigo', label: 'Sectigo' },
  { value: 'godaddy', label: 'GoDaddy' },
  { value: 'selfsigned', label: 'Autofirmado' },
  { value: 'other', label: 'Otro' },
];

const FORM_VACIO = {
  id: null,
  name: '',
  domain_id: '',
  common_name: '',
  issuer: '',
  cert_type: 'letsencrypt',
  serial_number: '',
  issued_at: '',
  expires_at: '',
  auto_renew: true,
  is_active: true,
  san: '',
  notes: '',
  issued_by_cmdb: false,
  issue_status: 'idle',
  issue_message: null,
  has_certificate: false,
  has_private_key: false,
};

function vencimiento(fecha) {
  if (!fecha) return null;
  const dias = Math.ceil((new Date(`${fecha}T23:59:59`) - Date.now()) / 86400000);
  return { dias, tono: dias < 0 ? 'bad' : dias <= 30 ? 'warn' : 'ok' };
}

const tonoEstado = { valid: 'ok', expiring: 'warn', expired: 'bad' };

export default function CertificateForm({
  certificate,
  mode = 'view',
  companyId,
  domains = [],
  siblings = [],
  isSuperadmin = false,
  onClose,
  onSaved,
  onDeleted,
  onNavigate,
}) {
  const isCards = getServerFormStyle() === 'cards';
  const empresaValida = Boolean(companyId) && companyId !== 'all';
  const sinIdInicial = mode === 'create' || !certificate?.id;

  const [form, setForm] = useState(() => ({ ...FORM_VACIO, ...(certificate || {}) }));
  const [base, setBase] = useState(() => ({ ...FORM_VACIO, ...(certificate || {}) }));
  const [editing, setEditing] = useState(sinIdInicial);
  const [tab, setTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [staging, setStaging] = useState(false);
  const [lanzando, setLanzando] = useState(false);

  const sf = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));
  const esAlta = !form.id;

  const instantanea = (obj) => JSON.stringify({
    name: obj.name || '',
    domain_id: String(obj.domain_id || ''),
    common_name: obj.common_name || '',
    issuer: obj.issuer || '',
    cert_type: obj.cert_type || '',
    serial_number: obj.serial_number || '',
    issued_at: obj.issued_at || '',
    expires_at: obj.expires_at || '',
    auto_renew: Boolean(obj.auto_renew),
    is_active: Boolean(obj.is_active),
    san: obj.san || '',
    notes: obj.notes || '',
  });
  const dirty = instantanea(form) !== instantanea(base);

  useEffect(() => {
    if (!certificate?.id) {
      setForm({ ...FORM_VACIO, ...(certificate || {}) });
      setBase({ ...FORM_VACIO, ...(certificate || {}) });
      setEditing(mode !== 'view');
      return undefined;
    }
    let cancelado = false;
    setError('');
    (async () => {
      try {
        const detalle = await api.getCertificate(certificate.id, companyId);
        if (cancelado) return;
        setForm({ ...FORM_VACIO, ...detalle });
        setBase({ ...FORM_VACIO, ...detalle });
        setEditing(mode !== 'view');
      } catch (err) {
        if (!cancelado) setError(err.message || 'No se pudo cargar el certificado');
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificate?.id, companyId, mode]);

  // La emisión ACME corre en el backend en segundo plano: se sondea el estado.
  useEffect(() => {
    if (!form.id || form.issue_status !== 'issuing') return undefined;
    let cancelado = false;
    const timer = setInterval(async () => {
      try {
        const detalle = await api.getCertificate(form.id, companyId);
        if (cancelado) return;
        setForm((prev) => ({ ...prev, ...detalle }));
        setBase((prev) => ({ ...prev, ...detalle }));
        if (detalle.issue_status === 'ok') {
          setAviso("Certificado emitido con Let's Encrypt");
          onSaved?.();
        } else if (detalle.issue_status === 'error') {
          setError(detalle.issue_message || "La emisión con Let's Encrypt falló");
          onSaved?.();
        }
      } catch {
        /* el siguiente intento lo reintenta */
      }
    }, 5000);
    return () => { cancelado = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.id, form.issue_status, companyId]);

  const emitir = async () => {
    if (!form.id) return;
    if (dirty) {
      setError('Guarda los cambios antes de emitir');
      return;
    }
    if (!form.domain_id) {
      setError('Vincula el certificado a un dominio de la CMDB antes de emitir');
      return;
    }
    if (!form.common_name) {
      setError('Escribe el nombre común (CN) antes de emitir');
      return;
    }
    setLanzando(true);
    setError('');
    setAviso('');
    try {
      await api.issueCertificate(form.id, { staging });
      setForm((prev) => ({ ...prev, issue_status: 'issuing', issue_message: null }));
      setAviso('Emisión lanzada: creando el TXT en OVH y esperando la propagación…');
    } catch (err) {
      setError(err.message || 'No se pudo lanzar la emisión');
    } finally {
      setLanzando(false);
    }
  };

  const descargar = async (what) => {
    if (!form.id) return;
    setError('');
    try {
      const nombre = await api.downloadCertificate(form.id, what);
      setAviso(`Descargado: ${nombre}`);
    } catch (err) {
      setError(err.message || 'No se pudo descargar');
    }
  };

  const guardar = async () => {
    if (!empresaValida) {
      setError('Selecciona una empresa en la barra superior antes de guardar');
      return;
    }
    if (!form.name.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        domain_id: form.domain_id || null,
        common_name: form.common_name,
        issuer: form.issuer,
        cert_type: form.cert_type,
        serial_number: form.serial_number,
        issued_at: form.issued_at || null,
        expires_at: form.expires_at || null,
        auto_renew: Boolean(form.auto_renew),
        is_active: Boolean(form.is_active),
        san: form.san,
        notes: form.notes,
      };
      const guardado = form.id
        ? await api.updateCertificate(form.id, payload, companyId)
        : await api.createCertificate(payload, companyId);
      // Al crear, la ficha tiene que quedarse con el id devuelto: si no, no se
      // puede emitir el certificado sin cerrar y volver a abrirlo.
      const fusion = {
        ...form,
        ...payload,
        ...(guardado || {}),
        domain_id: guardado?.domain_id ? String(guardado.domain_id) : '',
        issued_at: guardado?.issued_at || payload.issued_at || '',
        expires_at: guardado?.expires_at || payload.expires_at || '',
      };
      setForm(fusion);
      setBase(fusion);
      setEditing(false);
      setAviso('Certificado guardado');
      onSaved?.();
    } catch (err) {
      setError(err.message || 'No se pudo guardar el certificado');
    } finally {
      setSaving(false);
    }
  };

  const descartar = () => {
    setForm({ ...base });
    setError('');
    if (!form.id) onClose?.();
    else setEditing(false);
  };

  const borrar = async () => {
    if (!form.id) return;
    setConfirmDelete(false);
    try {
      await api.deleteCertificate(form.id, companyId);
      onSaved?.();
      onDeleted?.();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el certificado');
    }
  };

  const ven = vencimiento(form.expires_at);
  const emisionEnCurso = lanzando || form.issue_status === 'issuing';
  const estadoTonoActual = tonoEstado[form.status] || null;
  const badges = [
    {
      text: form.status_label || (form.is_active ? 'Activo' : 'Inactivo'),
      tone: form.is_active === false ? 'bad' : (estadoTonoActual || undefined),
      color: form.is_active !== false && !estadoTonoActual ? '#16a34a' : undefined,
    },
  ];
  if (ven) {
    badges.push({
      text: ven.dias < 0 ? `Caducado hace ${Math.abs(ven.dias)} d` : `Caduca en ${ven.dias} d`,
      tone: ven.tono === 'ok' ? undefined : ven.tono,
      color: ven.tono === 'ok' ? '#16a34a' : undefined,
    });
  }

  const indice = siblings.findIndex((fila) => String(fila.id) === String(form.id));
  const total = siblings.length;
  const irA = (paso) => {
    const destino = siblings[indice + paso];
    if (indice < 0 || !destino) return;
    if (dirty && !window.confirm('Tienes cambios sin guardar en este certificado. ¿Descartarlos y pasar al siguiente?')) return;
    onNavigate?.(destino);
  };

  const tabs = [{ key: 'general', label: 'General' }];

  const campo = (etiqueta, clave, placeholder, opciones = {}) => (
    <div className="sf2-field" style={opciones.full ? { gridColumn: '1 / -1' } : undefined}>
      <label>{etiqueta}</label>
      {editing ? (
        opciones.textarea ? (
          <textarea
            rows={opciones.rows || 3}
            value={form[clave] ?? ''}
            placeholder={placeholder}
            onChange={(e) => sf(clave, e.target.value)}
          />
        ) : opciones.select ? (
          <select value={form[clave] ?? ''} onChange={(e) => sf(clave, e.target.value)}>
            {opciones.select}
          </select>
        ) : (
          <input
            type={opciones.type || 'text'}
            value={form[clave] ?? ''}
            placeholder={placeholder}
            onChange={(e) => sf(clave, e.target.value)}
          />
        )
      ) : (
        <div className="v" style={opciones.textarea ? { whiteSpace: 'pre-wrap' } : undefined}>
          {form[clave] || '—'}
        </div>
      )}
    </div>
  );

  return (
    <div className="dv-overlay">
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon style={{ width: 15, height: 15 }} /> Certificados
        </button>
        <DetailCrumb label={form.name || 'Nuevo certificado'} />
        {!esAlta && total > 0 && (
          <div className="dv-pager">
            <button className="dv-pager-btn" type="button" onClick={() => irA(-1)} disabled={indice <= 0}>‹</button>
            <span className="dv-pager-count">{indice >= 0 ? indice + 1 : 1} / {total}</span>
            <button className="dv-pager-btn" type="button" onClick={() => irA(1)} disabled={indice < 0 || indice >= total - 1}>›</button>
          </div>
        )}
        <div className="dv-topbar-spacer" />
        {error && <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 360 }}>⚠ {error}</span>}
        <div className="dv-topbar-actions">
          {!editing ? (
            <>
              {isSuperadmin && !esAlta && (
                <>
                  <button
                    className={`dv-btn ${form.has_certificate ? 'dv-btn-secondary' : 'dv-btn-primary'}`}
                    type="button"
                    onClick={emitir}
                    disabled={emisionEnCurso || dirty}
                    title={dirty
                      ? 'Guarda los cambios antes de emitir'
                      : form.has_certificate
                        ? 'Pedir un certificado nuevo a Let\'s Encrypt'
                        : 'Emitir con Let\'s Encrypt por DNS-01 en OVH'}
                  >
                    {emisionEnCurso
                      ? <span className="spinner-border spinner-border-sm" />
                      : <ArrowPathIcon style={{ width: 14, height: 14 }} />}
                    {emisionEnCurso ? 'Emitiendo…' : form.has_certificate ? 'Renovar' : "Emitir Let's Encrypt"}
                  </button>
                  {form.has_certificate && (
                    <button
                      className="dv-btn dv-btn-secondary"
                      type="button"
                      onClick={() => descargar('cert')}
                      title="Descargar la cadena pública (.crt)"
                    >
                      <ArrowDownTrayIcon style={{ width: 14, height: 14 }} /> Certificado
                    </button>
                  )}
                  {form.has_private_key && (
                    <button
                      className="dv-btn dv-btn-secondary"
                      type="button"
                      onClick={() => descargar('key')}
                      title="Descargar la clave privada (queda registrado en auditoría)"
                    >
                      <KeyIcon style={{ width: 14, height: 14 }} /> Clave
                    </button>
                  )}
                </>
              )}
              {!esAlta && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)}>
                  <PencilSquareIcon style={{ width: 14, height: 14 }} /> Editar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
              </button>
            </>
          ) : (
            <>
              {!esAlta && (
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  title="Eliminar el certificado"
                >
                  <TrashIcon style={{ width: 14, height: 14 }} /> Eliminar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={descartar} disabled={saving}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Descartar
              </button>
              <button
                className="dv-btn dv-btn-primary"
                type="button"
                onClick={guardar}
                disabled={saving || (!esAlta && !dirty)}
                title={dirty ? 'Guardar los cambios' : 'Sin cambios'}
              >
                {saving ? <span className="spinner-border spinner-border-sm" /> : <CheckIcon style={{ width: 14, height: 14 }} />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="dv-body">
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={(e) => e.preventDefault()}>
          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<ShieldCheckIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(valor) => sf('name', valor)}
                placeholder="Certificado de ejemplo.com"
                editing={editing}
                saving={saving}
                onSave={guardar}
                onCancel={descartar}
                onEdit={() => setEditing(true)}
                crumb={[form.domain_name, TIPOS.find((t) => t.value === form.cert_type)?.label]
                  .filter(Boolean).join(' · ')}
                badges={badges}
                chips={form.common_name ? [{ value: form.common_name, label: 'CN' }] : []}
                dirty={dirty}
                inline
                titleDirty={dirty}
                onTitleCommit={() => { if (form.id) guardar(); }}
                onTitleReset={() => sf('name', base.name)}
              />
            ) : null}
            tabs={(
              <div className="dv-tabs-nav">
                {tabs.map((t) => (
                  <TabButton
                    key={t.key}
                    type="button"
                    className={`dv-tab-btn${tab === t.key ? ' active' : ''}`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                  </TabButton>
                ))}
              </div>
            )}
          >
            {tab === 'general' && (
              <div className="dv-tab-pane sf2-pane">
                <fieldset className="dv-fieldset" disabled={!editing}>
                  <div className="sf2-grid sf2-grid--2">
                    <div className="sf2-card">
                      <h3>Identificación</h3>
                      <div className="sf2-fields" style={{ '--n': 2 }}>
                        {campo('Nombre *', 'name', 'Certificado web de ejemplo.com', { full: true })}
                        <div className="sf2-field">
                          <label>Dominio</label>
                          {editing ? (
                            <select value={form.domain_id || ''} onChange={(e) => sf('domain_id', e.target.value)}>
                              <option value="">— sin dominio —</option>
                              {domains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                          ) : <div className="v">{form.domain_name || '—'}</div>}
                        </div>
                        {campo('Nombre común (CN)', 'common_name', 'www.ejemplo.com')}
                        {campo('Emitido por', 'issuer', "Let's Encrypt R3, DigiCert…")}
                        <div className="sf2-field">
                          <label>Tipo</label>
                          {editing ? (
                            <select value={form.cert_type} onChange={(e) => sf('cert_type', e.target.value)}>
                              {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                          ) : <div className="v">{TIPOS.find((t) => t.value === form.cert_type)?.label || '—'}</div>}
                        </div>
                        {campo('Nº de serie', 'serial_number', 'Opcional')}
                      </div>
                    </div>

                    <div className="sf2-card">
                      <h3>Renovación</h3>
                      <div className="sf2-fields" style={{ '--n': 2 }}>
                        {campo('Emitido el', 'issued_at', '', { type: 'date' })}
                        {campo('Caduca el', 'expires_at', '', { type: 'date' })}
                        <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                          <label>Queda</label>
                          {ven ? (
                            <div className="v">
                              <span className={`sf2-pill ${ven.tono === 'bad' ? 'sf2-pill--bad' : ven.tono === 'warn' ? 'sf2-pill--wa' : 'sf2-pill--ok'}`}>
                                {ven.dias < 0 ? `${Math.abs(ven.dias)} días vencido` : `${ven.dias} días`}
                              </span>
                            </div>
                          ) : <div className="v">—</div>}
                        </div>
                        <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                          <label>Renovación automática</label>
                          <label className="sf2-switch sf2-switch--sm">
                            <input
                              type="checkbox"
                              checked={Boolean(form.auto_renew)}
                              onChange={(e) => sf('auto_renew', e.target.checked)}
                              disabled={!editing}
                            />
                            <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                            <span className="sf2-switch-label">{form.auto_renew ? 'Se renueva sola' : 'Renovación manual'}</span>
                          </label>
                        </div>
                        <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                          <label>Estado</label>
                          <label className="sf2-switch sf2-switch--sm">
                            <input
                              type="checkbox"
                              checked={Boolean(form.is_active)}
                              onChange={(e) => sf('is_active', e.target.checked)}
                              disabled={!editing}
                            />
                            <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                            <span className="sf2-switch-label">{form.is_active ? 'En uso' : 'Fuera de uso'}</span>
                          </label>
                        </div>
                        {isSuperadmin && !form.has_certificate && (
                          <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                            <label>Entorno de emisión</label>
                            <label className="sf2-switch sf2-switch--sm">
                              <input
                                type="checkbox"
                                checked={staging}
                                onChange={(e) => setStaging(e.target.checked)}
                              />
                              <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                              <span className="sf2-switch-label">
                                {staging ? 'Staging (pruebas, no válido en navegadores)' : "Producción (Let's Encrypt)"}
                              </span>
                            </label>
                          </div>
                        )}
                        {(form.issued_by_cmdb || form.issue_status !== 'idle') && (
                          <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                            <label>Emisión desde la CMDB</label>
                            <div className="v">
                              <span className={`sf2-pill ${form.issue_status === 'error' ? 'sf2-pill--bad' : form.issue_status === 'issuing' ? 'sf2-pill--wa' : 'sf2-pill--ok'}`}>
                                {form.issue_status_label || 'Sin emitir'}
                              </span>
                              {form.last_renew_at && (
                                <span className="list-hint"> · última renovación {String(form.last_renew_at).slice(0, 10)}</span>
                              )}
                              {form.acme_staging && <span className="list-hint"> · staging</span>}
                            </div>
                          </div>
                        )}
                      </div>
                      {form.issue_status === 'error' && form.issue_message && (
                        <div className="sf2-hint" style={{ color: '#b91c1c' }}>
                          Let's Encrypt: {form.issue_message}
                        </div>
                      )}
                      {aviso && <div className="sf2-hint">{aviso}</div>}
                    </div>

                    <div className="sf2-card" style={{ gridColumn: '1 / -1' }}>
                      <h3>Nombres alternativos y notas</h3>
                      <div className="sf2-fields" style={{ '--n': 1 }}>
                        {campo('Nombres alternativos (SAN)', 'san', 'Uno por línea: www.ejemplo.com, api.ejemplo.com…', { textarea: true, rows: 3 })}
                        {campo('Notas', 'notes', 'Dónde se instala, quién lo renueva…', { textarea: true, rows: 3 })}
                      </div>
                    </div>
                  </div>
                </fieldset>
              </div>
            )}
            <FormDirtyDock
              show={isCards && dirty}
              saving={saving}
              onDiscard={descartar}
              onSave={guardar}
            />
          </FormCardsShell>
        </form>
      </div>

      {confirmDelete && (
        <Dialog
          title="Eliminar certificado"
          icon={<TrashIcon style={{ width: 18, height: 18 }} />}
          maxWidth={480}
          onClose={() => setConfirmDelete(false)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setConfirmDelete(false)}>
                Cancelar
              </button>
              <button type="button" className="list-btn-primary" onClick={borrar}>
                Eliminar
              </button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            ¿Eliminar «{form.name}» del inventario? No se toca el certificado real.
          </p>
        </Dialog>
      )}
    </div>
  );
}
